import HeaderV2 from "@/components/site/HeaderV2";
import Footer from "@/components/site/Footer";
import PageHero from "@/components/site/PageHero";
import SeoHead from "@/components/SeoHead";

import ClosingCTA from "@/components/site/ClosingCTA";
import FoundationDiagramAct3 from "@/components/site/FoundationDiagramAct3";
import Reveal from "@/components/site/Reveal";

import BuildMockup from "@/components/site/product/BuildMockup";

import DeployMockup from "@/components/site/product/DeployMockup";
import GovernMockup from "@/components/site/product/GovernMockup";
import IntegrationFanOut from "@/components/site/visuals/IntegrationFanOut";
import MetricCounters from "@/components/site/visuals/MetricCounters";
import InfrastructureArchitecture from "@/components/site/PlatformArchitecture";
import UserAtCenter from "@/components/site/UserAtCenter";
import type { ReactNode } from "react";
import {
  Boxes, FileCode2, Package, Code2,
  Network as NetworkIcon, ServerCog, LayoutGrid, Stethoscope,
  Activity, FileSearch, AlertTriangle, ShieldCheck,
  type LucideIcon,
} from "lucide-react";

type PillarBullet = { label: string; icon: LucideIcon };

interface PillarSectionProps {
  id: string;
  eyebrow: string;
  title: ReactNode;
  body: ReactNode;
  bullets: PillarBullet[];
  mockup: ReactNode;
  tightTop?: boolean;
}

const PillarSection = ({ id, eyebrow, title, body, bullets, mockup, tightTop }: PillarSectionProps) => {
  return (
    <section id={id} className="scroll-mt-24 border-t border-border">
      <div className={tightTop ? "container pt-6 md:pt-8 pb-24 md:pb-32" : "container py-24 md:py-32"}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">
          <Reveal className="lg:col-span-6">
            <p className="eyebrow">{eyebrow}</p>
            <h2 className="display mt-5 text-section-title text-foreground">{title}</h2>
            <div className="mt-8 space-y-6 text-base md:text-lg text-foreground/75 leading-relaxed">
              {body}
            </div>
          </Reveal>

          <Reveal className="lg:col-span-6 h-full" delay={0.15}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 h-full auto-rows-fr">
              {bullets.map(({ label, icon: Icon }) => (
                <div key={label} className="h-full p-6 border border-border rounded-lg bg-card hover:border-primary/40 transition-colors flex flex-col">
                  <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <p className="mt-5 text-sm md:text-base text-foreground font-medium leading-snug">
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="mt-16 md:mt-20">{mockup}</div>
        </Reveal>
      </div>
    </section>
  );
};

const InfrastructureLayer = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead collectionKey="page.solution" path="/solution" />
      <HeaderV2 />
      <main>
        <PageHero
          eyebrow="CLINICAL AI INFRASTRUCTURE"
          title={
            <>
              One integration. Every AI workflow across your{" "}
              <span className="italic font-normal text-primary/95">health system</span>.
            </>
          }
          subtitle="Connect deepc once to the clinical systems you already run. Then build, deploy, and govern AI workflows across the organization - without rebuilding integration, monitoring, and governance for every new capability."
          variant="dark"
          backdrop
        />

        {/* Signature integration moment */}
        <section id="integration" className="scroll-mt-24 border-t border-border bg-background">
          <div className="container py-24 md:py-32">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              <Reveal className="lg:col-span-5">
                <p className="eyebrow">How it works</p>
                <h2 className="display mt-5 text-section-title text-foreground">
                  One secure integration. A <span className="italic font-normal text-primary/95">reusable</span> layer for every AI workflow.
                </h2>
                <p className="mt-8 text-base md:text-lg text-foreground/75 leading-relaxed">
                  The integration to your clinical and operational systems happens once, under your security
                  and data-handling posture. Every workflow that follows runs on the same infrastructure layer
                  - with shared integration, monitoring, and governance.
                </p>
              </Reveal>

              <Reveal className="lg:col-span-7" delay={0.1}>
                <div className="relative">
                  <IntegrationFanOut />
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Bridge: from integration to workflow */}
        <section className="border-t border-border bg-muted/30">
          <div className="container pt-16 md:pt-20 pb-6 md:pb-8">
            <Reveal className="max-w-2xl mx-auto text-center">
              <p className="eyebrow">From integration to workflow</p>
              <h2 className="display mt-5 text-section-title text-foreground">
                Build, deploy, and govern on{" "}
                <span className="italic font-normal text-primary/95">one foundation.</span>
              </h2>
              <p className="mt-8 text-base md:text-lg text-foreground/75 leading-relaxed">
                Once deepc is connected, your teams can bring commercial, in-house, and agentic AI
                workflows into production without starting a new implementation project every time.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 md:mt-10 max-w-5xl mx-auto relative rounded-2xl border border-border bg-background shadow-sm p-8 md:p-12 lg:p-16 overflow-hidden">
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(80% 50% at 50% 0%, hsl(var(--primary) / 0.05), transparent 70%)",
                  }}
                />
                <div className="relative">
                  <FoundationDiagramAct3 />
                </div>
              </div>
            </Reveal>
            <div className="mx-auto mt-8 md:mt-10 max-w-3xl rounded-lg border border-border bg-background/60 px-4 py-3 text-center">
              <p className="text-xs md:text-sm leading-relaxed text-foreground/75">
                Workflows shown are illustrative. deepc partners with health systems to configure AI workflows, agents, integrations, and governance around their existing clinical environment.
              </p>
            </div>
          </div>
        </section>

        <PillarSection
          id="build"
          tightTop
          eyebrow="Build"
          title={<>Bring <span className="italic font-normal text-primary/95">any AI workflow</span> online without one-off projects</>}
          body={
            <>
              <p>
                With deepc infrastructure in place, new AI workflows are onboarded as software
                - not as implementation projects. Commercial, in-house, and research AI capabilities
                plug into the same foundation.
              </p>
              <p>
                Standardized inputs and outputs, reproducible packaging, and one interface for
                clinical, technical, and governance teams.
              </p>
            </>
          }
          bullets={[
            { label: "Onboard any AI workflow", icon: Boxes },
            { label: "Consistent inputs and outputs across every model", icon: FileCode2 },
            { label: "Deployable anywhere, every time", icon: Package },
            { label: "Workflow builder and API interface", icon: Code2 },
          ]}
          mockup={<BuildMockup />}
        />


        <PillarSection
          id="deploy"
          eyebrow="Deploy"
          title={<>Reach clinicians inside the <span className="italic font-normal text-primary/95">workflows</span> they already use.</>}
          body={
            <>
              <p>
                Because the integration to clinical, imaging, documentation, workflow, and operational systems
                already exists, deploying a new workflow becomes configuration - not re-integration.
                AI appears where care happens, across sites and teams.
              </p>
              <p>
                Managed runtime, monitored behavior, and predictable performance
                across every site.
              </p>
            </>
          }
          bullets={[
            { label: "EHR, imaging, documentation, worklists", icon: NetworkIcon },
            { label: "Managed runtime infrastructure", icon: ServerCog },
            { label: "Multi-site consistency", icon: LayoutGrid },
            { label: "Clinician-native delivery", icon: Stethoscope },
          ]}
          mockup={<DeployMockup />}
        />

        <PillarSection
          id="govern"
          eyebrow="Govern"
          title={<>One source of truth for every AI workflow in <span className="italic font-normal text-primary/95">production.</span></>}
          body={
            <>
              <p>
                Continuous oversight, audit, and control across every deployed
                workflow. Performance, drift, usage, incidents, adoption, and evidence
                - visible in one place, with the evidence trail your institution needs to stand
                behind AI in routine care.
              </p>
              <p>
                Built around the regulatory, institutional, and clinical governance
                frameworks health systems are accountable to.
              </p>
            </>
          }
          bullets={[
            { label: "Continuous performance monitoring", icon: Activity },
            { label: "Full audit traceability", icon: FileSearch },
            { label: "Drift and incident detection", icon: AlertTriangle },
            { label: "Regulatory and institutional governance alignment", icon: ShieldCheck },
          ]}
          mockup={<GovernMockup />}
        />


        {/* Metric counters strip */}
        <section className="border-t border-border bg-muted/20">
          <div className="container py-20 md:py-28">
            <Reveal className="max-w-2xl mb-12">
              <p className="eyebrow">By the numbers</p>
              <h2 className="display mt-5 text-subsection text-foreground">
                Infrastructure operating in production.
              </h2>
            </Reveal>
            <MetricCounters
              metrics={[
                { value: 250, suffix: "+", label: "pre-built connectors and integrations" },
                { value: 95, suffix: "%", label: "less implementation effort" },
                { value: 6, prefix: "≤", suffix: "w", label: "to value realization" },
                { value: 1000, suffix: "+", label: "hospitals and care sites covered" },
              ]}
            />
          </div>
        </section>

        <InfrastructureArchitecture />

        <UserAtCenter />

        <ClosingCTA />
      </main>
      <Footer />
    </div>
  );
};

export default InfrastructureLayer;
